import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

function OutBilling({ toggleSidebar }) {
  const [responseData, setResponseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [showEntries, setShowEntries] = useState("10");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  // New state for filter type
  const [filterType, setFilterType] = useState("All"); // Default to show all data
  const [dateFilter, setDateFilter] = useState("today");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");

  const navigate = useNavigate();

  // Fetch user privileges and billing data (unchanged)
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);
      if (userRoleId === "unknown") {
        setError("No role assigned to the user. Please contact the administrator.");
        setLoadingPrivileges(false);
        return;
      }
      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      const roleData = response.data.find((role) => role.role_id === userRoleId);

      if (roleData) {
        const privileges = {};
        roleData.pages.forEach((page) => {
          privileges[page.page] = {
            access: page.access,
            ...page.priviallages,
          };
        });
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message);
      setError("Failed to fetch user privileges. Please try again.");
    } finally {
      setLoadingPrivileges(false);
    }
  };

  const hasPermission = (permission) => {
    if (!userPrivileges || !userPrivileges["OUT Billing"]) {
      return false;
    }
    const privileges = userPrivileges["OUT Billing"];
    if (permission === "access") {
      return privileges.access === true;
    }
    if (permission in privileges && privileges[permission] === false) {
      return false;
    }
    return privileges.access && (privileges.all || privileges[permission]);
  };

const fetchData = async () => {
  if (!hasPermission("read")) {
    setError("You do not have permission to view billing records.");
    return;
  }
  setLoading(true);
  try {
    const response = await axios.get(`${Apiurl}/billing_management`);
    const filteredData = Array.isArray(response.data)
      ? response.data
          .filter((item) => item.category === "out")
          .reverse()
      : [];
    setResponseData(filteredData);
    setError(null);
  } catch (error) {
    console.error("Error fetching billing records:", error.message);
    setError("Failed to fetch billing records. Please try again.");
    setTimeout(() => setError(null), 3000);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (hasPermission("access") && hasPermission("read")) {
      fetchData();
    } else if (!loadingPrivileges) {
      setError("You do not have permission to access this page.");
    }
  }, [userPrivileges, loadingPrivileges]);


  const toDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getLocalDateKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const matchesDateFilter = (value) => {
    if (dateFilter === "all") return true;

    const itemDate = toDateKey(value);
    if (!itemDate) return false;

    const today = new Date();
    const todayKey = getLocalDateKey(today);

    if (dateFilter === "today") return itemDate === todayKey;

    if (dateFilter === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return itemDate === getLocalDateKey(yesterday);
    }

    if (dateFilter === "last_week") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "last_month") {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "custom") {
      if (!customFromDate && !customToDate) return true;
      if (customFromDate && itemDate < customFromDate) return false;
      if (customToDate && itemDate > customToDate) return false;
      return true;
    }

    return true;
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setActivePage(1);
  };

  // Updated filteredBilling with filterType logic
  const filteredBilling = useMemo(() => {
    return responseData.filter((item) => {
      const patientName = item.patients_details?.[0]
        ? `${item.patients_details[0].first_name || ""} ${
            item.patients_details[0].last_name || ""
          }`.trim()
        : "";

      // Apply button-based filter
      const matchesFilterType =
  filterType === "All" ||
  (filterType === "Homeo" && item.patient_id?.startsWith("4HH-")) ||
  (filterType === "Yoga" && item.patient_id?.startsWith("4HY-"));

      // Apply search term filter
      const matchesSearchTerm = [
        item.bill_invoice_no,
        item.bill_date,
        item.patient_id,
        item.doctor_id,
        item.department,
        item.payment_type,
        item.total_amount?.toString(),
        item.patients_details?.[0]?.first_name,
        item.patients_details?.[0]?.last_name,
        item.doctor_details?.[0]?.doctor_name,
        patientName,
      ].some((prop) =>
        prop != null && typeof prop === "string"
          ? prop.toLowerCase().includes(searchTerm.toLowerCase())
          : false
      );

      const matchesDate = matchesDateFilter(item.bill_date);

      return matchesFilterType && matchesSearchTerm && matchesDate;
    });
  }, [
    responseData,
    searchTerm,
    filterType,
    dateFilter,
    customFromDate,
    customToDate,
  ]);

  useEffect(() => {
    if (showEntries === "All") {
      setItemsPerPage(filteredBilling.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
    setActivePage(1);
  }, [showEntries, filteredBilling]);

  const totalPages = Math.ceil(filteredBilling.length / itemsPerPage) || 1;
  const indexOfLastItem = activePage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBilling.slice(indexOfFirstItem, indexOfLastItem);
  const startIndex = (activePage - 1) * itemsPerPage;

  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", activePage - 1, activePage, activePage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      setActivePage(page);
    }
  };

  const handleAddOutBillingClick = () => {
    if (hasPermission("create")) {
      toggleSidebar();
      navigate("/billing/out-billing/add-billing");
    } else {
      setError("You do not have permission to add billing records.");
    }
  };

  const openDeleteModal = (id) => {
    if (hasPermission("delete")) {
      if (!id) {
        setError("Invalid billing record ID.");
        return;
      }
      setDeleteId(id);
      setShowDeleteModal(true);
    } else {
      setError("You do not have permission to delete billing records.");
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      setError("You do not have permission to delete billing records.");
      return;
    }
    if (!deleteId) {
      setError("Invalid billing record ID.");
      return;
    }
    try {
      await axios.delete(`${Apiurl}/billing_management/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting billing record:", error.message);
      setError("Failed to delete billing record. Please try again.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEdit = (id) => {
    if (hasPermission("update")) {
      if (!id) {
        setError("Invalid billing record ID.");
        return;
      }
      toggleSidebar();
      navigate(`/billing/out-billing/update-billing/${id}`);
    } else {
      setError("You do not have permission to edit billing records.");
    }
  };
  
    useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleView = (id) => {
    if (hasPermission("view")) {
      if (!id) {
        setError("Invalid billing record ID.");
        return;
      }
      navigate(`/billing/view-billing/${id}`);
    } else {
      setError("You do not have permission to view billing records.");
    }
  };

  // const handlePrintFun = (bill_invoice_no) => {
  //   const url = `/billing/out-billing/add-billing/op-bill/${bill_invoice_no}`;
  //   const stateData = encodeURIComponent(JSON.stringify(bill_invoice_no));
  //   const printWindow = window.open(`${url}?data=${stateData}`, "_blank");
  //   if (!printWindow) {
  //     alert("Popup blocked. Please allow popups for this site.");
  //   }
  // };

  const handlePrintFun = (bill_invoice_no) => {
  const url = `/billing/out-billing/add-billing/op-bill/${bill_invoice_no}`;
  const stateData = encodeURIComponent(JSON.stringify(bill_invoice_no));

  // Wait for 4 seconds (4000 ms)
  setTimeout(() => {
    const printWindow = window.open(`${url}?data=${stateData}`, "_blank");
    if (!printWindow) {
      alert("Popup blocked. Please allow popups for this site.");
    }
  }, 500); // 4000ms = 4 seconds
};


  // Button click handlers for filtering
  const handleHomeoFilter = () => {
    console.log("Filtering by Homeo (4HH)");
    setFilterType("Homeo");
    setActivePage(1); // Reset to first page when filter changes
  };

  const handleYogaFilter = () => {
    console.log("Filtering by Yoga (4HY)");
    setFilterType("Yoga");
    setActivePage(1); // Reset to first page when filter changes
  };

  const handleResetFilter = () => {
    console.log("Resetting filter to All");
    setFilterType("All");
    setActivePage(1); // Reset to first page when filter changes
  };

  if (loadingPrivileges) {
    return <div className="p-3">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      {hasPermission("create") && (
        <div className="text-end">
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#01AEBE" }}
            onClick={handleAddOutBillingClick}
          >
            + Add Billing
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 crm-filter-tabs">
              {[
                ["today", "Today"],
                ["yesterday", "Yesterday"],
                ["last_week", "Last Week"],
                ["last_month", "Last Month"],
                ["all", "All"],
                ["custom", "Custom Date"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-sm ${
                    dateFilter === value ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handleDateFilterChange(value)}
                >
                  {label}
                </button>
              ))}

              {dateFilter === "custom" && (
                <div className="d-flex flex-wrap align-items-center gap-2 ms-md-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">From</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customFromDate}
                      onChange={(e) => {
                        setCustomFromDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">To</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customToDate}
                      onChange={(e) => {
                        setCustomToDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="row mb-3 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => setShowEntries(e.target.value)}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-2"></div>

             <div className="col-md-3">
  <div className="btn-group w-100 crm-filter-tabs" role="group">
    <button
      className={`btn btn-sm ${filterType === "All" ? "btn-success" : "btn-outline-success"}`}
      onClick={handleResetFilter}
    >
      All
    </button>
    <button
      className={`btn btn-sm ${filterType === "Homeo" ? "btn-primary" : "btn-outline-primary"}`}
      onClick={handleHomeoFilter}
    >
      Homeo
    </button>
    <button
      className={`btn btn-sm ${filterType === "Yoga" ? "btn-secondary" : "btn-outline-secondary"}`}
      onClick={handleYogaFilter}
    >
      Yoga
    </button>
  </div>
</div>

          

               <div className="col-md-2"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "35%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table responsive-table">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th>S.No</th>
                    <th>Invoice No</th>
                    <th>Patient ID</th>
                    <th>Bill Date</th>
                    <th>Patients Name</th>
                    <th>Doctor</th>
                    <th>Bill Amount</th>
                    {(hasPermission("view") ||
                      hasPermission("update") ||
                      hasPermission("delete")) && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={item._id || index} className="align-middle">
                        <td>{startIndex + index + 1}</td>
                        <td>{item.bill_invoice_no || "N/A"}</td>
                        <td>{item.patient_id || "N/A"}</td>
                        <td>{item.bill_date || "N/A"}</td>
                        <td>
                          {item.patients_details?.[0]?.first_name
                            ? `${item.patients_details[0].first_name} ${
                                item.patients_details[0].last_name || ""
                              }`
                            : "N/A"}
                        </td>
                        <td>
                          {item.doctor_details?.[0]?.doctor_name || "N/A"}
                        </td>
                        <td>{item.total_amount || "N/A"}</td>
                        {(hasPermission("view") ||
                          hasPermission("update") ||
                          hasPermission("delete")) && (
                          <td>
                            {hasPermission("delete") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => openDeleteModal(item._id)}
                              >
                                <Trash2 size={16} className="text-danger" />
                              </button>
                            )}
                            {hasPermission("update") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => handleEdit(item._id)}
                              >
                                <Edit size={16} className="text-primary" />
                              </button>
                            )}
                            <button
                              className="btn btn-sm"
                              onClick={() => handlePrintFun(item.bill_invoice_no)}
                            >
                              <Printer size={16} className="text-warning" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          hasPermission("view") ||
                          hasPermission("update") ||
                          hasPermission("delete")
                            ? 8
                            : 7
                        }
                        className="text-center py-3"
                      >
                        {loading ? "Loading..." : "No billing records found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredBilling.length > 0 && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage > 1 && handlePageClick(activePage - 1)
                    }
                    disabled={activePage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {getPageList().map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === activePage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === activePage
                            ? { backgroundColor: "#20c997", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage < totalPages && handlePageClick(activePage + 1)
                    }
                    disabled={activePage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteBillingModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteBillingModalLabel">
                  Delete Billing
                </h5>
                <p style={{ marginTop: "10px" }}>
                  You're going to delete this billing record. Are you sure?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && hasPermission("delete") && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default OutBilling;